import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { BienDetailPage } from "@/pages/biens/BienDetailPage";
import type { FicheTabId } from "@/components/biens/ficheTabs";
import { BienVenteDetailPage } from "@/v2/pages/BienVenteDetailPage";
import { useBien } from "@/hooks/useBiens";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { isForSale } from "@/lib/transaction";
import type { AppRole } from "@/contexts/RoleContext";
import type { FichePreset } from "@/contexts/fieldPermsEditor";

interface BienDetailModalProps {
  bienId: string | null;
  onClose: () => void;
  /**
   * Force le rôle effectif pour les permissions de lecture/écriture.
   *
   * Plus aucun appelant ne s'en sert depuis le 2026-08-10 : le workspace
   * Publication le passait à "publication_suivi", ce qui donnait à un même
   * bien deux jeux de droits selon qu'on l'ouvrait par la modale ou par
   * /v2/biens/:id. La migration 113 a supprimé le besoin. Conservé parce que
   * la prop reste le bon outil si une modale doit un jour émuler un rôle.
   */
  roleOverride?: AppRole;
  /** Préréglage de popup (Apparts for Rent / Pipeline ELS) — cf. BienDetailPageProps.preset. */
  preset?: FichePreset;
  /** Onglet ouvert au montage — cf. BienDetailPageProps.initialTab. Ignoré pour un bien en vente (BienVenteDetailPage n'a pas ces onglets). */
  initialTab?: FicheTabId;
}

/**
 * Modale fiche bien — montée par-dessus la page courante (Apparts for Rent,
 * Pipeline ELS, …) sans changer l'URL. L'utilisateur reste contextuellement
 * dans son workspace et ne perd pas la liste qu'il était en train de parcourir.
 *
 * Implémentation : on injecte `bienIdOverride` directement à `BienDetailPage`
 * pour court-circuiter `useParams().id` (l'URL extérieure pointe toujours sur
 * la page hôte, pas sur `/biens/:id`). Les hooks router de BienDetailPage
 * (useLocation, useNavigate, useListNavigation) continuent de s'appuyer sur
 * le BrowserRouter parent — il n'y a PAS de router imbriqué (react-router v7
 * refuse les routers nested).
 *
 * Pas de fil d'Ariane V2 : la modale est volontairement plus light que
 * V2BienDetailWrapper. Les bannières d'alertes (champs obligatoires /
 * recommandés manquants) restent visibles puisqu'elles sont rendues par
 * BienDetailPage elle-même.
 */
export function BienDetailModal({ bienId, onClose, roleOverride, preset, initialTab }: BienDetailModalProps) {
  const { t } = useTranslation();
  // Bifurcation location/vente (même logique que V2BienDetailWrapper) : le
  // workspace Publication liste aussi des biens en vente — la modale doit
  // monter LEUR fiche, pas la fiche location. Même query key que la fiche
  // montée ensuite → un seul fetch (cache partagé).
  const permRole = useBiensPermRole();
  const { data: bien, isLoading } = useBien(bienId ?? undefined, permRole);
  const forSale = isForSale(bien?.transaction);
  useEffect(() => {
    if (!bienId) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [bienId, onClose]);

  if (!bienId) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 overflow-y-auto py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-7xl mx-auto my-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          title={t("v2.bienDetailModal.closeTitle")}
          className="absolute top-3 right-3 z-10 p-2 rounded-md bg-surface border border-border text-text-muted hover:text-text-primary hover:bg-gray-100 cursor-pointer transition-colors outline-none shadow-none"
        >
          <X size={16} />
        </button>
        <div className="p-4 md:p-6 pt-4">
          {/* Référence interne + badge Location/Vente, miroir du fil d'Ariane
              de V2BienDetailWrapper que la modale n'a pas — sans ça on ne
              sait plus quel bien est ouvert (retour Thibaud 2026-09-16). */}
          {bien && (
            <div className="flex items-center gap-2 mb-4 pr-12 flex-wrap">
              <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                {bien.referenceInterne || bien.reference || ""}
              </span>
              {bien.transaction && (
                <Badge variant={forSale ? "vente" : "location"}>
                  {t(`biens.transactionType.${forSale ? "vente" : "location"}`)}
                </Badge>
              )}
            </div>
          )}
          {/* Tant que la transaction est inconnue, ne monter AUCUNE des deux
              fiches (évite un flash location → vente au premier chargement). */}
          {isLoading && !bien ? null : forSale ? (
            <BienVenteDetailPage bienIdOverride={bienId} />
          ) : (
            <BienDetailPage bienIdOverride={bienId} roleOverride={roleOverride} preset={preset} initialTab={initialTab} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
